import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import ExcelJS from 'exceljs';
import { IconPlus, IconSearch, IconTag, IconEdit, IconTrash } from './Icons';
import './Projects.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const projectsSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const defaultForm = () => ({
  title: '',
  client_name: '',
  department_id: '',
  project_manager_profile_id: '',
  price: '',
  cost: '',
  provision_profit: '',
  profit_percent: '',
  start_date: '',
  deadline: '',
  status: 'open',
  progress: 0,
  description: '',
});

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      projectsSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchProjects = useCallback(async (mountedRef) => {
    const isMounted = () => !mountedRef || mountedRef.current;
    setLoading(true);
    setError(null);
    try {
      const fetchPromise = projectsSupabase
        .from('projects')
        .select(`*, project_manager:profiles!project_manager_profile_id(id, full_name)`)
        .order('created_at', { ascending: false });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const result = await Promise.race([fetchPromise, timeoutPromise]);
      if (!isMounted()) return;
      const { data, error: err } = result;
      if (err) {
        setError(err.message);
        setProjects([]);
      } else {
        setProjects(data || []);
      }
    } catch (e) {
      if (isMounted()) {
        setError(e?.message || 'Unexpected error while loading projects.');
        setProjects([]);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, []);

  const fetchDepartments = useCallback(async (mountedRef) => {
    const isMounted = () => !mountedRef || mountedRef.current;
    try {
      const { data, error: err } = await projectsSupabase.from('departments').select('id, name').order('name', { ascending: true });
      if (!isMounted()) return;
      if (err) {
        console.error('[Projects] fetchDepartments error', err);
        setDepartments([]);
      } else {
        setDepartments(data || []);
      }
    } catch (e) {
      if (isMounted()) setDepartments([]);
    }
  }, []);

  const fetchManagers = useCallback(async (mountedRef) => {
    const isMounted = () => !mountedRef || mountedRef.current;
    try {
      const { data, error: err } = await projectsSupabase.from('profiles').select('id, full_name').order('full_name', { ascending: true, nullsFirst: false });
      if (!isMounted()) return;
      if (err) {
        console.error('[Projects] fetchManagers error', err);
        setManagers([]);
      } else {
        setManagers(data || []);
      }
    } catch (e) {
      if (isMounted()) setManagers([]);
    }
  }, []);

  const location = useLocation();
  const mountedRef = useRef(true);

  const refreshSessionAndFetch = useCallback(async () => {
    mountedRef.current = true;
    setError(null);
    try {
      await projectsSupabase.auth.getSession();
      await runWakeUp();
      await fetchProjects(mountedRef);
      if (!mountedRef.current) return;
      await fetchDepartments(mountedRef);
      await fetchManagers(mountedRef);
    } catch (e) {
      setError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchProjects, fetchDepartments, fetchManagers]);

  useEffect(() => {
    if (location.pathname !== '/projects') return;
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      const { error: sessionErr } = await projectsSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchProjects(mountedRef);
      if (cancelled) return;
      await fetchDepartments(mountedRef);
      await fetchManagers(mountedRef);
    })();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [location.pathname, runWakeUp, fetchProjects, fetchDepartments, fetchManagers]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const managersById = useMemo(
    () =>
      managers.reduce((acc, m) => {
        const key = m.id == null ? '' : String(m.id);
        if (key) acc[key] = m;
        return acc;
      }, {}),
    [managers],
  );

  const PROJECT_COLUMNS = [
    { key: 'id', label: 'ID' },
    { key: 'title', label: 'Title' },
    { key: 'department', label: 'Department' },
    { key: 'projectManager', label: 'Project manager' },
    { key: 'client', label: 'Client' },
    { key: 'price', label: 'Price' },
    { key: 'cost', label: 'Cost' },
    { key: 'provisionProfit', label: 'Provision Profit' },
    { key: 'profitPercent', label: 'Profit %' },
    { key: 'startDate', label: 'Start date' },
    { key: 'deadline', label: 'Deadline' },
    { key: 'progress', label: 'Progress' },
    { key: 'status', label: 'Status' },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    PROJECT_COLUMNS.reduce(
      (acc, col) => ({
        ...acc,
        [col.key]: true,
      }),
      {},
    ),
  );

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }),
    );
  };

  const visibleProjectColumns = PROJECT_COLUMNS.filter((col) => visibleColumns[col.key]);

  const filteredProjects = projects.filter((p) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (p.title || '').toLowerCase().includes(q) ||
      (p.client_name || '').toLowerCase().includes(q);

    const hasStart = !!startDateFilter;
    const hasEnd = !!endDateFilter;
    const projectDate = p.start_date || '';

    const matchesStart = !hasStart || (projectDate && projectDate >= startDateFilter);
    const matchesEnd = !hasEnd || (projectDate && projectDate <= endDateFilter);

    return matchesSearch && matchesStart && matchesEnd;
  });
  const totalCount = filteredProjects.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const currentPage = Math.min(page, totalPages);
  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );
  const from = totalCount === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const to = Math.min(currentPage * perPage, totalCount);

  const formatNum = (n) => (n != null && n !== '' ? Number(n).toFixed(3) : '');
  const formatPercent = (n) => (n != null && n !== '' ? Number(n).toFixed(2) : '');
  const statusLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
  const getDepartmentName = (id) => {
    if (!id) return '—';
    const found = departments.find((d) => d.id === id);
    return found?.name || '—';
  };
  const getManagerName = (project) => {
    const embedded = project.project_manager;
    if (embedded && (embedded.full_name != null && embedded.full_name !== '')) {
      return embedded.full_name;
    }
    const id = project.project_manager_profile_id;
    if (id == null || id === '') return '—';
    const profile = managersById[String(id)];
    if (profile && (profile.full_name != null && profile.full_name !== '')) {
      return profile.full_name;
    }
    return '—';
  };

  const openAddModal = () => {
    setForm(defaultForm());
    setSelectedProject(null);
    setShowAddModal(true);
  };

  const openEditModal = (project) => {
    setSelectedProject(project);
    setForm({
      title: project.title || '',
      client_name: project.client_name || '',
      department_id: project.department_id || '',
      project_manager_profile_id: project.project_manager_profile_id || '',
      price: project.price != null ? String(project.price) : '',
      cost: project.cost != null ? String(project.cost) : '',
      provision_profit: project.provision_profit != null ? String(project.provision_profit) : '',
      profit_percent: project.profit_percent != null ? String(project.profit_percent) : '',
      start_date: project.start_date || '',
      deadline: project.deadline || '',
      status: project.status || 'open',
      progress: project.progress ?? 0,
      description: project.description || '',
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (project) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const handleAddSubmit = async () => {
    console.log('[Projects] handleAddSubmit: start', { form });
    setSaving(true);
    const payload = {
      title: form.title.trim() || null,
      client_name: form.client_name.trim() || null,
      department_id: form.department_id || null,
      project_manager_profile_id: form.project_manager_profile_id || null,
      price: form.price !== '' ? parseFloat(form.price) : null,
      cost: form.cost !== '' ? parseFloat(form.cost) : null,
      provision_profit: form.provision_profit !== '' ? parseFloat(form.provision_profit) : null,
      profit_percent: form.profit_percent !== '' ? parseFloat(form.profit_percent) : null,
      start_date: form.start_date || null,
      deadline: form.deadline || null,
      status: (form.status || 'open').toLowerCase(),
      progress: form.progress != null ? Number(form.progress) : 0,
      description: form.description?.trim() || null,
      updated_at: new Date().toISOString(),
    };
    console.log('[Projects] handleAddSubmit: payload', payload);
    try {
      await runWakeUp();
      const { data, error: err } = await projectsSupabase.from('projects').insert(payload).select();
      console.log('[Projects] handleAddSubmit: response', { data, error: err });
      if (err) {
        setError(err.message);
        return;
      }
      setError(null);
      setShowAddModal(false);
      fetchProjects();
    } catch (e) {
      console.error('[Projects] handleAddSubmit: unexpected error', e);
      setError(e.message || 'Unexpected error while creating project.');
    } finally {
      setSaving(false);
      console.log('[Projects] handleAddSubmit: end');
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedProject?.id) {
      console.warn('[Projects] handleEditSubmit called with no selectedProject');
      return;
    }
    console.log('[Projects] handleEditSubmit: start', { id: selectedProject.id, form });
    setSaving(true);
    const payload = {
      title: form.title.trim() || null,
      client_name: form.client_name.trim() || null,
      department_id: form.department_id || null,
      project_manager_profile_id: form.project_manager_profile_id || null,
      price: form.price !== '' ? parseFloat(form.price) : null,
      cost: form.cost !== '' ? parseFloat(form.cost) : null,
      provision_profit: form.provision_profit !== '' ? parseFloat(form.provision_profit) : null,
      profit_percent: form.profit_percent !== '' ? parseFloat(form.profit_percent) : null,
      start_date: form.start_date || null,
      deadline: form.deadline || null,
      status: (form.status || 'open').toLowerCase(),
      progress: form.progress != null ? Number(form.progress) : 0,
      description: form.description?.trim() || null,
      updated_at: new Date().toISOString(),
    };
    console.log('[Projects] handleEditSubmit: payload', payload);
    try {
      await runWakeUp();
      const { data, error: err } = await projectsSupabase
        .from('projects')
        .update(payload)
        .eq('id', selectedProject.id)
        .select();
      console.log('[Projects] handleEditSubmit: response', { data, error: err });
      if (err) {
        setError(err.message);
        return;
      }
      setError(null);
      setShowEditModal(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (e) {
      console.error('[Projects] handleEditSubmit: unexpected error', e);
      setError(e.message || 'Unexpected error while updating project.');
    } finally {
      setSaving(false);
      console.log('[Projects] handleEditSubmit: end');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProject?.id) {
      console.warn('[Projects] handleDeleteConfirm called with no selectedProject');
      return;
    }
    console.log('[Projects] handleDeleteConfirm: start', { id: selectedProject.id });
    setSaving(true);
    try {
      await runWakeUp();
      const { data, error: err } = await projectsSupabase
        .from('projects')
        .delete()
        .eq('id', selectedProject.id)
        .select();
      console.log('[Projects] handleDeleteConfirm: response', { data, error: err });
      if (err) {
        setError(err.message);
        return;
      }
      setError(null);
      setShowDeleteModal(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (e) {
      console.error('[Projects] handleDeleteConfirm: unexpected error', e);
      setError(e.message || 'Unexpected error while deleting project.');
    } finally {
      setSaving(false);
      console.log('[Projects] handleDeleteConfirm: end');
    }
  };

  const getExportCellString = (row, col) => {
    switch (col.key) {
      case 'id':
        return String(row.id ?? '');
      case 'title':
        return row.title || '';
      case 'department':
        return getDepartmentName(row.department_id);
      case 'projectManager':
        return getManagerName(row);
      case 'client':
        return row.client_name || '';
      case 'price':
        return formatNum(row.price) || '';
      case 'cost':
        return formatNum(row.cost) || '';
      case 'provisionProfit':
        return formatNum(row.provision_profit) || '';
      case 'profitPercent':
        return formatPercent(row.profit_percent)
          ? `${formatPercent(row.profit_percent)}%`
          : '';
      case 'startDate':
        return row.start_date || '';
      case 'deadline':
        return row.deadline || '';
      case 'progress': {
        const pct = row.progress ?? 0;
        return `${Math.min(100, Math.max(0, Number(pct)))}%`;
      }
      case 'status':
        return statusLabel(row.status);
      default:
        return '';
    }
  };

  const handleExportExcel = async () => {
    const exportColumns = visibleProjectColumns;
    if (!filteredProjects.length || !exportColumns.length || exporting) return;

    setExporting(true);
    setError(null);

    const brandHeader = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB45309' } };
    const brandHeaderFont = { color: { argb: 'FFFFFFFF' }, bold: true, name: 'Calibri', size: 11 };
    const brandTitleFont = { bold: true, size: 16, color: { argb: 'FFB45309' }, name: 'Calibri' };
    const brandSubtitleFont = { size: 10, color: { argb: 'FF6B7280' }, name: 'Calibri' };
    const borderThin = {
      top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
    };
    const zebraFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7ED' } };
    const colWidthByKey = {
      id: 14,
      title: 32,
      department: 22,
      projectManager: 22,
      client: 24,
      price: 12,
      cost: 12,
      provisionProfit: 16,
      profitPercent: 12,
      startDate: 12,
      deadline: 12,
      progress: 10,
      status: 12,
    };

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'ECDT Portal';
      workbook.created = new Date();
      const sheet = workbook.addWorksheet('Projects', {
        properties: { tabColor: { argb: 'FFB45309' } },
      });

      try {
        const logoUrl = `${process.env.PUBLIC_URL || ''}/logo.png`;
        const res = await fetch(logoUrl);
        if (res.ok) {
          const blob = await res.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const imageId = workbook.addImage({ buffer: arrayBuffer, extension: 'png' });
          sheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 180, height: 48 } });
        }
      } catch (e) {
        console.warn('[Projects] Excel logo fetch failed', e);
      }

      const numCols = exportColumns.length;
      const titleRowIndex = 4;
      const titleRow = sheet.getRow(titleRowIndex);
      titleRow.getCell(1).value = 'Projects';
      titleRow.height = 22;
      titleRow.font = brandTitleFont;
      sheet.mergeCells(titleRowIndex, 1, titleRowIndex, numCols);

      const subRowIndex = titleRowIndex + 1;
      const subRow = sheet.getRow(subRowIndex);
      subRow.getCell(1).value = `ECDT Portal · Exported ${new Date().toLocaleString()}`;
      subRow.font = brandSubtitleFont;
      sheet.mergeCells(subRowIndex, 1, subRowIndex, numCols);

      const headerRowIndex = subRowIndex + 2;
      const headerRow = sheet.getRow(headerRowIndex);
      exportColumns.forEach((col, idx) => {
        headerRow.getCell(idx + 1).value = col.label;
      });
      headerRow.height = 20;
      headerRow.eachCell({ includeEmpty: false }, (cell) => {
        cell.fill = brandHeader;
        cell.font = brandHeaderFont;
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        cell.border = borderThin;
      });

      let rowIndex = headerRowIndex + 1;
      filteredProjects.forEach((row, i) => {
        const excelRow = sheet.getRow(rowIndex);
        exportColumns.forEach((col, idx) => {
          const cell = excelRow.getCell(idx + 1);
          cell.value = getExportCellString(row, col);
          cell.font = { name: 'Calibri', size: 11, color: { argb: 'FF1F2937' } };
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          cell.border = borderThin;
          if (i % 2 === 1) {
            cell.fill = zebraFill;
          }
        });
        excelRow.height = 18;
        rowIndex += 1;
      });

      sheet.columns = exportColumns.map((c) => ({ width: colWidthByKey[c.key] ?? 14 }));

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `projects-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[Projects] export failed', e);
      setError(e?.message || 'Export failed. Try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="projects-page">
      <div className="projects-card">
        <div className="projects-header-row">
          <h1 className="projects-title">Projects</h1>
          <div className="projects-header-actions">
            <button
              type="button"
              className="projects-header-btn primary"
              onClick={openAddModal}
            >
              <IconPlus />
              Add project
            </button>
          </div>
        </div>

        <div className="projects-filter-row">
          <div className="projects-filter-left">
            <div className="projects-column-toggle-wrap">
              <button
                type="button"
                className="projects-columns-toggle"
                aria-label="Columns"
                onClick={() => setColumnsMenuOpen((open) => !open)}
              >
                <span className="projects-column-dots">●●●</span>
              </button>

              {columnsMenuOpen && (
                <div className="projects-column-menu">
                  {PROJECT_COLUMNS.map((col) => (
                    <button
                      key={col.key}
                      type="button"
                      className={`projects-column-menu-item ${
                        visibleColumns[col.key] ? 'active' : ''
                      }`}
                      onClick={() => toggleColumn(col.key)}
                    >
                      <span>{col.label}</span>
                      {visibleColumns[col.key] && (
                        <span className="projects-column-check">✓</span>
                      )}
                    </button>
                  ))}
                  <div className="projects-column-menu-divider" />
                  <div className="projects-column-menu-footer">
                    <IconTag />
                  </div>
                </div>
              )}
            </div>
            <div className="projects-date-filters">
              <label className="projects-date-filter">
                <span>Start date</span>
                <input
                  type="date"
                  value={startDateFilter}
                  onChange={(e) => {
                    setStartDateFilter(e.target.value);
                    setPage(1);
                  }}
                />
              </label>
              <label className="projects-date-filter">
                <span>End date</span>
                <input
                  type="date"
                  value={endDateFilter}
                  onChange={(e) => {
                    setEndDateFilter(e.target.value);
                    setPage(1);
                  }}
                />
              </label>
            </div>
          </div>
          <div className="projects-filter-right">
            <button
              type="button"
              className="projects-utility-btn"
              onClick={handleExportExcel}
              disabled={exporting || !filteredProjects.length || !visibleProjectColumns.length}
            >
              {exporting ? 'Exporting…' : 'Export Excel'}
            </button>
            <div className="projects-search">
              <input
                type="text"
                placeholder="Search"
                aria-label="Search projects"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              <span className="projects-search-icon">
                <IconSearch />
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="projects-error" role="alert">
            {error}
          </div>
        )}
        {loading ? (
          <div className="projects-loading">Loading projects…</div>
        ) : (
        <div className="projects-table-wrapper">
          <table className="projects-table">
            <thead>
              <tr>
                <th />{/* actions */}
                {visibleProjectColumns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedProjects.length === 0 ? (
                <tr>
                  <td colSpan={visibleProjectColumns.length + 1} className="projects-empty-cell">
                    No projects found.
                  </td>
                </tr>
              ) : (
                paginatedProjects.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="projects-row-actions">
                        <button
                          type="button"
                          className="projects-row-icon-btn"
                          onClick={() => openEditModal(row)}
                          aria-label="Edit project"
                        >
                          <IconEdit />
                        </button>
                        <button
                          type="button"
                          className="projects-row-icon-btn"
                          onClick={() => openDeleteModal(row)}
                          aria-label="Delete project"
                        >
                          <IconTrash />
                        </button>
                      </div>
                    </td>
                    {visibleProjectColumns.map((col) => {
                      if (col.key === 'id') {
                        return <td key={col.key}>{String(row.id).slice(0, 8)}…</td>;
                      }
                      if (col.key === 'title') {
                        return (
                          <td key={col.key}>
                            <span className="projects-table-link">{row.title || '—'}</span>
                          </td>
                        );
                      }
                      if (col.key === 'department') {
                        return <td key={col.key}>{getDepartmentName(row.department_id)}</td>;
                      }
                      if (col.key === 'projectManager') {
                        return <td key={col.key}>{getManagerName(row)}</td>;
                      }
                      if (col.key === 'client') {
                        return (
                          <td key={col.key}>
                            <span className="projects-client-link">{row.client_name || '—'}</span>
                          </td>
                        );
                      }
                      if (col.key === 'price') {
                        return <td key={col.key}>{formatNum(row.price)}</td>;
                      }
                      if (col.key === 'cost') {
                        return <td key={col.key}>{formatNum(row.cost)}</td>;
                      }
                      if (col.key === 'provisionProfit') {
                        return <td key={col.key}>{formatNum(row.provision_profit)}</td>;
                      }
                      if (col.key === 'profitPercent') {
                        return <td key={col.key}>{formatPercent(row.profit_percent)}</td>;
                      }
                      if (col.key === 'startDate') {
                        return <td key={col.key}>{row.start_date || '—'}</td>;
                      }
                      if (col.key === 'deadline') {
                        return (
                          <td key={col.key} style={{ color: '#ef4444' }}>
                            {row.deadline || '—'}
                          </td>
                        );
                      }
                      if (col.key === 'progress') {
                        const pct = row.progress ?? 0;
                        return (
                          <td key={col.key}>
                            <div className="projects-progress-track">
                              <div
                                className="projects-progress-fill"
                                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                              />
                            </div>
                          </td>
                        );
                      }
                      if (col.key === 'status') {
                        return (
                          <td key={col.key}>
                            <span className="projects-status-pill">{statusLabel(row.status)}</span>
                          </td>
                        );
                      }
                      return null;
                    })}
                    <td>
                      <div className="projects-row-actions">
                        <button
                          type="button"
                          className="projects-row-icon-btn"
                          onClick={() => openEditModal(row)}
                          aria-label="Edit project"
                        >
                          <IconEdit />
                        </button>
                        <button
                          type="button"
                          className="projects-row-icon-btn"
                          onClick={() => openDeleteModal(row)}
                          aria-label="Delete project"
                        >
                          <IconTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {paginatedProjects.length > 0 && (
                <tr className="projects-total-row">
                  <td />{/* actions column empty in totals row */}
                  {visibleProjectColumns.map((col, index) => {
                    if (index === 0) {
                      return (
                        <td key={col.key} className="projects-total-label">
                          Total
                        </td>
                      );
                    }
                    if (col.key === 'price') {
                      const sum = paginatedProjects.reduce((s, p) => s + (Number(p.price) || 0), 0);
                      return <td key={col.key}>{formatNum(sum)}</td>;
                    }
                    if (col.key === 'cost') {
                      const sum = paginatedProjects.reduce((s, p) => s + (Number(p.cost) || 0), 0);
                      return <td key={col.key}>{formatNum(sum)}</td>;
                    }
                    if (col.key === 'provisionProfit') {
                      const sum = paginatedProjects.reduce((s, p) => s + (Number(p.provision_profit) || 0), 0);
                      return <td key={col.key}>{formatNum(sum)}</td>;
                    }
                    if (col.key === 'profitPercent') {
                      const sum = paginatedProjects.reduce((s, p) => s + (Number(p.profit_percent) || 0), 0);
                      return <td key={col.key}>{formatPercent(sum)}</td>;
                    }
                    return <td key={col.key} />;
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        <div className="projects-footer-row">
          <div className="projects-footer-left">
            <select
              className="projects-page-size"
              aria-label="Rows per page"
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span>{from}-{to} / {totalCount}</span>
          </div>
          <div className="projects-footer-right">
            <button
              type="button"
              className="projects-page-btn"
              aria-label="Previous page"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              «
            </button>
            <button type="button" className="projects-page-btn" aria-label="Current page" disabled>
              {currentPage}
            </button>
            <button
              type="button"
              className="projects-page-btn"
              aria-label="Next page"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              »
            </button>
          </div>
        </div>
      </div>

      {/* Add project modal */}
      {showAddModal && (
        <div className="estimates-modal-backdrop" onClick={() => setShowAddModal(false)}>
          <div
            className="estimates-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Add project</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => setShowAddModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="estimates-modal-body">
              <div className="estimates-form-grid">
                <div className="estimates-form-label">Title</div>
                <input
                  className="estimates-form-input"
                  placeholder="Project title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
                <div className="estimates-form-label">Client</div>
                <input
                  className="estimates-form-input"
                  placeholder="Client name"
                  value={form.client_name}
                  onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
                />

                <div className="estimates-form-label">Department</div>
                <select
                  className="estimates-form-select"
                  value={form.department_id}
                  onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
                >
                  <option value="">- Select department -</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>

                <div className="estimates-form-label">Project manager</div>
                <select
                  className="estimates-form-select"
                  value={form.project_manager_profile_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, project_manager_profile_id: e.target.value }))
                  }
                >
                  <option value="">- Select project manager -</option>
                  {managers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.full_name || m.id}
                    </option>
                  ))}
                </select>

                <div className="estimates-form-label">Description</div>
                <textarea
                  className="estimates-form-textarea"
                  placeholder="Description"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />

                <div className="estimates-form-label">Start date</div>
                <input
                  className="estimates-form-input"
                  type="date"
                  placeholder="Start date"
                  value={form.start_date}
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                />

                <div className="estimates-form-label">Deadline</div>
                <input
                  className="estimates-form-input"
                  type="date"
                  placeholder="Deadline"
                  value={form.deadline}
                  onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))}
                />

                <div className="estimates-form-label">Price</div>
                <input
                  className="estimates-form-input"
                  type="number"
                  step="any"
                  placeholder="Price"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                />

                <div className="estimates-form-label">Cost</div>
                <input
                  className="estimates-form-input"
                  type="number"
                  step="any"
                  placeholder="Cost"
                  value={form.cost}
                  onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))}
                />

                <div className="estimates-form-label">Provision profit</div>
                <input
                  className="estimates-form-input"
                  type="number"
                  step="any"
                  placeholder="Provision profit"
                  value={form.provision_profit}
                  onChange={(e) => setForm((f) => ({ ...f, provision_profit: e.target.value }))}
                />

                <div className="estimates-form-label">Profit %</div>
                <input
                  className="estimates-form-input"
                  type="number"
                  step="any"
                  placeholder="Profit %"
                  value={form.profit_percent}
                  onChange={(e) => setForm((f) => ({ ...f, profit_percent: e.target.value }))}
                />

                <div className="estimates-form-label">Progress (0–100)</div>
                <input
                  className="estimates-form-input"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  value={form.progress}
                  onChange={(e) => setForm((f) => ({ ...f, progress: e.target.value === '' ? 0 : Number(e.target.value) }))}
                />
              </div>
            </div>

            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => setShowAddModal(false)}
                disabled={saving}
              >
                Close
              </button>
              <button
                type="button"
                className="estimates-footer-btn estimates-footer-btn-primary"
                onClick={handleAddSubmit}
                disabled={saving || !form.title?.trim()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit project modal */}
      {showEditModal && selectedProject && (
        <div className="estimates-modal-backdrop" onClick={() => setShowEditModal(false)}>
          <div
            className="estimates-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Edit project</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => setShowEditModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="estimates-modal-body">
              <div className="estimates-form-grid">
                <div className="estimates-form-label">Title</div>
                <input
                  className="estimates-form-input"
                  placeholder="Project title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
                <div className="estimates-form-label">Client</div>
                <input
                  className="estimates-form-input"
                  placeholder="Client name"
                  value={form.client_name}
                  onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
                />

                <div className="estimates-form-label">Department</div>
                <select
                  className="estimates-form-select"
                  value={form.department_id}
                  onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
                >
                  <option value="">- Select department -</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>

                <div className="estimates-form-label">Project manager</div>
                <select
                  className="estimates-form-select"
                  value={form.project_manager_profile_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, project_manager_profile_id: e.target.value }))
                  }
                >
                  <option value="">- Select project manager -</option>
                  {managers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.full_name || m.id}
                    </option>
                  ))}
                </select>

                <div className="estimates-form-label">Description</div>
                <textarea
                  className="estimates-form-textarea"
                  placeholder="Description"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />

                <div className="estimates-form-label">Start date</div>
                <input
                  className="estimates-form-input"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                />

                <div className="estimates-form-label">Deadline</div>
                <input
                  className="estimates-form-input"
                  type="date"
                  value={form.deadline}
                  onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))}
                />

                <div className="estimates-form-label">Price</div>
                <input
                  className="estimates-form-input"
                  type="number"
                  step="any"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                />

                <div className="estimates-form-label">Cost</div>
                <input
                  className="estimates-form-input"
                  type="number"
                  step="any"
                  value={form.cost}
                  onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))}
                />

                <div className="estimates-form-label">Provision profit</div>
                <input
                  className="estimates-form-input"
                  type="number"
                  step="any"
                  value={form.provision_profit}
                  onChange={(e) => setForm((f) => ({ ...f, provision_profit: e.target.value }))}
                />

                <div className="estimates-form-label">Profit %</div>
                <input
                  className="estimates-form-input"
                  type="number"
                  step="any"
                  value={form.profit_percent}
                  onChange={(e) => setForm((f) => ({ ...f, profit_percent: e.target.value }))}
                />

                <div className="estimates-form-label">Progress (0–100)</div>
                <input
                  className="estimates-form-input"
                  type="number"
                  min="0"
                  max="100"
                  value={form.progress}
                  onChange={(e) => setForm((f) => ({ ...f, progress: e.target.value === '' ? 0 : Number(e.target.value) }))}
                />

                <div className="estimates-form-label">Status</div>
                <select
                  className="estimates-form-select"
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => setShowEditModal(false)}
                disabled={saving}
              >
                Close
              </button>
              <button
                type="button"
                className="estimates-footer-btn estimates-footer-btn-primary"
                onClick={handleEditSubmit}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteModal && selectedProject && (
        <div className="estimates-modal-backdrop" onClick={() => setShowDeleteModal(false)}>
          <div
            className="estimates-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Delete?</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => setShowDeleteModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="estimates-modal-body">
              <p>Are you sure you want to delete &quot;{selectedProject.title || 'this project'}&quot;? You won&apos;t be able to undo this action.</p>
            </div>
            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn projects-btn-blue"
                onClick={handleDeleteConfirm}
                disabled={saving}
              >
                {saving ? 'Deleting…' : 'Delete'}
              </button>
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => setShowDeleteModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

